import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Robot, Sparkle, Copy, Printer, MagnifyingGlass, Warning, ArrowClockwise, BookOpenText } from "@phosphor-icons/react";
import { toast } from "sonner";
import DraftLibrary from "@/components/DraftLibrary";
import InterviewMode from "@/components/InterviewMode";

const LANGUAGES = ["English", "Marathi", "Hindi", "Marathi + English", "Hindi + English"];
const TONES = ["Formal Legal", "Neutral", "Simple"];
const DRAFT_STYLES = ["Standard", "Professional", "Court Format"];
const STATES = ["Maharashtra", "Gujarat", "Karnataka", "Madhya Pradesh", "Delhi", "Other"];

export default function AiAssistant() {
  const [tab, setTab] = useState("interview"); // interview | generate | library
  const [customOpen, setCustomOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [rentAgreementId, setRentAgreementId] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [language, setLanguage] = useState("English");
  const [tone, setTone] = useState("Formal Legal");
  const [draftStyle, setDraftStyle] = useState("Standard");
  const [stateName, setStateName] = useState("Maharashtra");
  const [manualDraft, setManualDraft] = useState(false);
  const [manualName, setManualName] = useState("");
  const [tplSearch, setTplSearch] = useState("");
  const [instructions, setInstructions] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [models, setModels] = useState([]);
  const [modelKey, setModelKey] = useState("anthropic|claude-sonnet-4-6");
  const outRef = useRef(null);
  const abortRef = useRef(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/ai/templates").then((r) => setGroups(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/ai/models").then((r) => setModels(r.data));
  }, []);

  // Pre-fill from URL params (from Rent Agreement / Customer profile / Dashboard AI shortcuts)
  useEffect(() => {
    const t = params.get("template"); const c = params.get("customer"); const ra = params.get("ra");
    const tabParam = params.get("t");
    if (tabParam === "library" || tabParam === "generate" || tabParam === "interview") setTab(tabParam);
    if (t) setTemplateId(t);
    if (c) setCustomerId(c);
    if (ra) setRentAgreementId(ra);
  }, [params]);

  const filteredCustomers = useMemo(() => {
    if (!custSearch) return customers.slice(0, 100);
    const q = custSearch.toLowerCase();
    return customers.filter((c) =>
      c.name?.toLowerCase().includes(q) || c.mobile?.includes(q) || c.customer_no?.toLowerCase().includes(q)
    ).slice(0, 100);
  }, [custSearch, customers]);

  const selectedTemplate = useMemo(() => {
    for (const g of groups) {
      const t = g.items.find((x) => x.id === templateId);
      if (t) return t;
    }
    return null;
  }, [templateId, groups]);

  const generate = async () => {
    if (manualDraft ? !manualName.trim() : !templateId) { toast.error(manualDraft ? "Enter the draft name" : "Select a template first"); return; }
    setOutput(""); setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("csc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/draft/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          template_id: manualDraft ? "manual" : templateId,
          manual_draft_name: manualDraft ? manualName.trim() : "",
          customer_id: customerId === "_none" ? "" : customerId,
          customer_name: (customers.find((c) => c.id === customerId)?.name) || "",
          rent_agreement_id: rentAgreementId || "",
          variables: {},
          custom_instructions: instructions || "",
          language,
          tone,
          draft_style: draftStyle,
          state: stateName,
          model_provider: modelKey.split("|")[0],
          model_name: modelKey.split("|")[1],
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          let ev = "message", data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) ev = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trimStart();
          }
          if (ev === "message" && data) {
            const decoded = data.replace(/\\n/g, "\n");
            acc += decoded;
            setOutput(acc);
            if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
          } else if (ev === "error") {
            toast.error(data);
          } else if (ev === "done") {
            // stream finished
          }
        }
      }
      toast.success("Draft ready — please review before printing");
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Generation failed");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const cancel = () => { abortRef.current?.abort(); setStreaming(false); };
  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copied"); };
  const doPrint = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked"); return; }
    // Build DOM safely — textContent auto-escapes and prevents XSS.
    const pre = w.document.createElement("pre");
    pre.style.fontFamily = "'IBM Plex Sans',sans-serif";
    pre.style.fontSize = "13px";
    pre.style.whiteSpace = "pre-wrap";
    pre.style.padding = "32px";
    pre.style.maxWidth = "720px";
    pre.style.margin = "auto";
    pre.textContent = output;
    w.document.body.appendChild(pre);
    w.document.title = selectedTemplate?.name || "Draft";
    w.print();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2 flex items-center gap-2">
            <Sparkle size={14} weight="fill" /> AI Assistant
          </div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>AI Draft Studio</h1>
          <p className="text-sm text-slate-600 mt-2">Generate legal drafts in seconds · Manage every draft in one library with versions and exports.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6" data-testid="ai-tabs">
        <button
          onClick={() => setTab("interview")}
          data-testid="ai-tab-interview"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${tab === "interview" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
        >
          <Robot size={14} weight={tab === "interview" ? "fill" : "regular"} /> AI Interview
        </button>
        <button
          onClick={() => setTab("generate")}
          data-testid="ai-tab-generate"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${tab === "generate" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
        >
          <Sparkle size={14} weight={tab === "generate" ? "fill" : "regular"} /> Manual Prompt
        </button>
        <button
          onClick={() => setTab("library")}
          data-testid="ai-tab-library"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${tab === "library" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
        >
          <BookOpenText size={14} /> Draft Library
        </button>
        <button
          onClick={() => setCustomOpen(true)}
          data-testid="ai-tab-custom"
          className="ml-auto inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors"
        >
          <Sparkle size={14} weight="fill" /> My Templates
        </button>
      </div>

      <CustomTemplatesDialog open={customOpen} onOpenChange={setCustomOpen} onSaved={() => api.get("/ai/templates").then((r) => setGroups(r.data))} />

      {tab === "library" ? (
        <DraftLibrary />
      ) : tab === "interview" ? (
        <InterviewMode />
      ) : (
      <>
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mb-6 flex items-start gap-2 text-sm text-amber-900">
        <Warning size={16} weight="duotone" className="mt-0.5 shrink-0" />
        <span><b>AI-assisted draft.</b> Always review before printing or submission. AI may make mistakes with legal formatting.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: config */}
        <Card className="lg:col-span-1 p-5 bg-white border-slate-200 space-y-4">
          <F label="Template *">
            <div className="mb-2 flex items-center gap-2">
              <button
                type="button"
                data-testid="ai-manual-toggle"
                onClick={() => { setManualDraft(!manualDraft); setTemplateId(""); }}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  manualDraft ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                }`}
              >
                ✎ Other (Manual Typing)
              </button>
            </div>
            {manualDraft ? (
              <>
                <Input data-testid="ai-manual-name" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Draft name — e.g. Society Transfer Undertaking" />
                <div className="text-[11px] text-amber-700 mt-1">AI will infer the correct Indian legal structure for this document.</div>
              </>
            ) : (
            <>
            <Input value={tplSearch} onChange={(e) => setTplSearch(e.target.value)} placeholder="Search 130+ drafts…" className="h-9 mb-2" data-testid="ai-template-search" />
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="ai-template-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {groups.map((g) => {
                  const items = g.items.filter((t) => !tplSearch || t.name.toLowerCase().includes(tplSearch.toLowerCase()));
                  if (!items.length) return null;
                  return (
                  <div key={g.group}>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-600 bg-slate-50">{g.group}</div>
                    {items.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </div>
                  );
                })}
              </SelectContent>
            </Select>
            </>
            )}
          </F>
          {selectedTemplate && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">What to provide</div>
              {selectedTemplate.hint}
            </div>
          )}

          <F label="Customer (auto-fills details)">
            <div className="relative mb-2">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search…" className="pl-8 h-9" />
            </div>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="ai-customer-select"><SelectValue placeholder="Optional — pick to auto-fill" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_none">— None —</SelectItem>
                {filteredCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name} · {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Language">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="ai-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tone">
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Draft Style">
              <Select value={draftStyle} onValueChange={setDraftStyle}>
                <SelectTrigger data-testid="ai-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DRAFT_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="State">
              <Select value={stateName} onValueChange={setStateName}>
                <SelectTrigger data-testid="ai-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>

          <F label="AI Model">
            <Select value={modelKey} onValueChange={setModelKey}>
              <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {models.map((m) => {
                  const key = `${m.provider}|${m.name}`;
                  const dot = m.provider === "anthropic" ? "bg-orange-500" : m.provider === "openai" ? "bg-emerald-500" : "bg-blue-500";
                  return (
                    <SelectItem key={key} value={key}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${dot}`}></span>
                        <span className="font-medium">{m.label}</span>
                        <span className="text-[10px] text-slate-500 ml-1">{m.hint}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </F>

          <F label="Additional Instructions">
            <Textarea rows={4} data-testid="ai-instructions" placeholder="e.g., Include witness clause; mention specific dates…" value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </F>

          {!streaming ? (
            <Button data-testid="ai-generate-btn" onClick={generate} disabled={manualDraft ? !manualName.trim() : !templateId} className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Sparkle size={16} weight="fill" /> Generate Draft
            </Button>
          ) : (
            <Button onClick={cancel} variant="outline" className="w-full h-11 gap-2 border-red-300 text-red-700 hover:bg-red-50">
              <ArrowClockwise size={16} className="animate-spin" /> Stop
            </Button>
          )}
        </Card>

        {/* Right: output */}
        <Card className="lg:col-span-2 p-5 bg-white border-slate-200 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center"><Robot size={16} weight="duotone" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Draft</div>
                <div className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{manualDraft ? (manualName || "Manual Draft") : (selectedTemplate?.name || "AI Output")}</div>
              </div>
              {streaming && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-600 animate-pulse" /> Streaming…</Badge>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!output} onClick={copy} className="gap-1"><Copy size={14} /> Copy</Button>
              <Button size="sm" variant="outline" disabled={!output} onClick={doPrint} className="gap-1"><Printer size={14} /> Print</Button>
            </div>
          </div>

          <div
            ref={outRef}
            data-testid="ai-output"
            className="flex-1 min-h-[420px] max-h-[560px] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800 whitespace-pre-wrap font-mono leading-relaxed"
          >
            {output || <span className="text-slate-400">Draft will appear here. Select a template and click Generate.</span>}
          </div>
        </Card>
      </div>
      </>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}


// ==================================================================
// Custom Template Creator dialog
// ==================================================================
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash, FloppyDisk, Plus, Star } from "@phosphor-icons/react";

const TEMPLATE_GROUPS = [
  "Custom Drafts", "Affidavit", "NOC", "Rent Agreement", "Legal",
  "Government", "Business", "Banking", "Office", "Complaint",
  "Society Letter", "GST Letter", "Passport Letter", "Police Letter",
  "Company Documents", "HR",
];

function CustomTemplatesDialog({ open, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", group: "Custom Drafts", hint: "", prompt: "", language: "English", favorite: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/ai/templates/custom")).data); } catch {}
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/ai/templates/custom", form);
      toast.success("Template saved");
      setForm({ name: "", group: "Custom Drafts", hint: "", prompt: "", language: "English", favorite: false });
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this custom template?")) return;
    try { await api.delete(`/ai/templates/custom/${id}`); await load(); onSaved?.(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white" data-testid="custom-templates-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-orange-500" /> My Custom Templates
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 rounded-lg border border-slate-200 bg-slate-50 space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1">
              <Plus size={11} /> Create new template
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[11px] font-semibold text-slate-600">Template Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 mt-1" placeholder="e.g. Society NOC — Wagle Estate" data-testid="custom-name" /></div>
              <div><Label className="text-[11px] font-semibold text-slate-600">Category</Label>
                <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="custom-group"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64 bg-white">{TEMPLATE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[11px] font-semibold text-slate-600">Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="English">English</SelectItem><SelectItem value="Marathi">Marathi</SelectItem><SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Marathi + English">Marathi + English</SelectItem><SelectItem value="Hindi + English">Hindi + English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[11px] font-semibold text-slate-600">Fields (comma separated)</Label><Input value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })} className="h-9 mt-1" placeholder="landlord, tenant, rent, deposit" /></div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Prompt (optional — extra AI instructions)</Label>
              <Textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={3} className="resize-none mt-1" placeholder="Include a receipt block at the bottom. Language must be Marathi. Include witness signatures." data-testid="custom-prompt" />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" checked={form.favorite} onChange={(e) => setForm({ ...form, favorite: e.target.checked })} /> <Star size={13} weight="fill" className="text-yellow-500" /> Mark as favorite</label>
              <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5 h-9" data-testid="custom-save"><FloppyDisk size={12} weight="fill" /> {saving ? "Saving…" : "Save Template"}</Button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Your Templates ({rows.length})</div>
            <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="custom-list">
              {rows.length === 0 && <div className="text-center py-8 text-sm text-slate-400 italic">No custom templates yet. Save your first above ↑</div>}
              {rows.map((t) => (
                <div key={t.id} className="p-3 rounded-md border border-slate-200 flex items-start gap-3" data-testid={`custom-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      {t.favorite && <Star size={12} weight="fill" className="text-yellow-500" />}
                      {t.name}
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.group}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.hint || t.prompt || "—"}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{t.language} · by {t.created_by}</div>
                  </div>
                  <button onClick={() => del(t.id)} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`custom-del-${t.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
